'use client';

import { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'cta' | 'primary' | 'outline-primary' | 'outline-white' | 'purple';
export type ButtonSize = 'default' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  isLoading?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  cta: 'bg-[#E0DF00] text-[#373A36] hover:bg-[#00A19B] hover:text-white disabled:hover:bg-[#E0DF00] disabled:hover:text-[#373A36]',
  primary:
    'bg-[#00A19B] text-white hover:bg-[#007d78] disabled:hover:bg-[#00A19B] disabled:hover:text-white',
  'outline-primary':
    'bg-transparent border-2 border-[#E8E8E4] text-[#6b6e68] hover:border-[#00A19B] hover:text-[#00A19B] disabled:hover:border-[#E8E8E4] disabled:hover:text-[#6b6e68]',
  'outline-white':
    'bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#373A36] disabled:hover:bg-transparent disabled:hover:text-white',
  purple:
    'bg-[#612166] text-white hover:bg-[#4a1a4f] disabled:hover:bg-[#612166] disabled:hover:text-white',
};

const sizeStyles: Record<ButtonSize, string> = {
  default: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
};

export function Button({
  variant = 'cta',
  size = 'default',
  iconLeft,
  iconRight,
  isLoading,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      disabled={disabled || isLoading}
      aria-disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center gap-2.5 rounded-full font-bold tracking-wide
        transition-all duration-200 motion-reduce:transition-none
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
      {...props}
    >
      {isLoading && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {!isLoading && iconLeft}
      <span>{children}</span>
      {!isLoading && iconRight}
    </button>
  );
}

export default Button;
